import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  XdsBannerComponent,
  XdsButtonComponent,
  XdsIconButtonComponent,
  XdsSelectOption,
  XdsTileRadioContentDirective,
  XdsTileRadioGroupComponent,
} from 'src/app/shared/ui';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { ClientLoggerService } from 'src/app/core/services/logging/client-logger.service';
import { LogCategory } from 'src/app/core/services/logging/client-log.models';
import { environment } from 'src/environments/environment';
import { AuthLegalFooterComponent } from '../../components/auth-legal-footer/auth-legal-footer.component';
import { DemoService } from '../../services/demo/demo.service';
import {
  DemoStartAttribution,
  demoAttributionFromQueryParams,
} from '../../services/demo/demo-start-attribution.model';
import {
  DEMO_INDUSTRIES,
  DemoIndustry,
  DemoIndustryId,
  demoIndustryFromLandingPage,
  demoIndustryFromParam,
} from '../../services/demo/demo-industry.model';

/** Minimaler Ausschnitt der Cloudflare-Turnstile-API (analog register-user.component). */
interface TurnstileApi {
  render(container: string | HTMLElement, options: Record<string, unknown>): string;
  remove(widgetId: string): void;
  reset(widgetId?: string): void;
}

/**
 * Zustand der Bot-Prüfung — sichtbar gemacht, weil ein stumm gesperrter Startknopf der teuerste
 * Zustand dieser Seite ist.
 *
 * Vorher gab es nur „Token da / Token nicht da": Schlug Turnstile fehl (Werbeblocker, strenger
 * Tracking-Schutz, abgebrochenes Skript), setzte der `error-callback` das Token still auf `null`.
 * Das Widget läuft mit `interaction-only`, ist also unsichtbar — der Besucher sah einen grauen
 * Knopf ohne jede Begründung und ging. Am 19.08.2026 im Zugriffsprotokoll belegt: Seite geladen,
 * ~35 s Aufenthalt, **kein** `POST /api/Demo/Start`.
 *
 * `interactive` ist ein eigener Zustand, weil dann NICHT der Zeitgeber greifen darf: Turnstile
 * wartet in diesem Fall bewusst auf den Menschen.
 */
export type DemoCaptchaState = 'not-required' | 'loading' | 'interactive' | 'ready' | 'failed';

/**
 * Wartezeit, nach der eine nicht abgeschlossene Prüfung als fehlgeschlagen gilt (ms).
 * Turnstile meldet einen blockierten Skript-Abruf nicht zuverlässig zurück — ohne diese Grenze
 * bleibt die Seite endlos im Zustand „läuft" und damit im stummen Sperrzustand von vorher.
 */
const CAPTCHA_TIMEOUT_MS = 12_000;

/**
 * Vollbild-Willkommens-Seite für den Self-Service-Demo-Einstieg (`/demo`).
 *
 * Erste Seite, auf der ein Interessent landet (Demo-Build, Root-Redirect). Kommuniziert **offen**,
 * dass es sich um eine 48-Stunden-**Wegwerf-Vorschau** mit Beispieldaten handelt und Änderungen NICHT
 * in ein späteres Konto übernommen werden. Rendert bei Bedarf das Turnstile-Widget (Bot-Schutz) und
 * startet auf Bestätigung die Demo via {@link DemoService}; bei Erfolg navigiert der AuthService in die
 * App. Die Turnstile-Logik entspricht der aus register-user/dem früheren Demo-Start-Dialog.
 */
@Component({
  selector: 'app-demo-landing',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    XdsButtonComponent,
    XdsIconButtonComponent,
    XdsBannerComponent,
    XdsTileRadioGroupComponent,
    XdsTileRadioContentDirective,
    AuthLegalFooterComponent,
  ],
  templateUrl: './demo-landing.component.html',
  styleUrls: ['./demo-landing.component.css'],
})
export class DemoLandingComponent implements AfterViewInit, OnDestroy {
  isSubmitting = false;

  /** Sichtbarer Zustand der Bot-Prüfung; ohne Site-Key ist nichts zu prüfen. */
  captchaState: DemoCaptchaState = environment.turnstileSiteKey ? 'loading' : 'not-required';

  private turnstileScript?: HTMLScriptElement;
  private turnstileWidgetId: string | null = null;
  private turnstileRenderAttempts = 0;
  private turnstileToken: string | null = null;
  private captchaTimeoutId: ReturnType<typeof setTimeout> | null = null;
  /** Ab dem ersten Fehlversuch wird die Challenge sichtbar gerendert (siehe `retryCaptcha`). */
  private forceVisibleWidget = false;
  private readonly attribution: DemoStartAttribution;

  /** Auswählbare Branchenprofile (Reihenfolge = Reihenfolge der Kacheln). */
  readonly industries: readonly DemoIndustry[] = DEMO_INDUSTRIES;

  /**
   * Kacheln für `xds-tile-radio-group`.
   *
   * Bewusst ein **stabiles Feld** und kein Getter: `xds-tile-radio-group` ist OnPush und
   * ControlValueAccessor — eine neue Array-Referenz je Änderungserkennung führt dort in eine
   * Endlosschleife (siehe `shared/ui/CLAUDE.md`, „stabiles Feld, nie Template-Funktionsaufruf").
   */
  readonly industryOptions: XdsSelectOption<DemoIndustryId>[] = DEMO_INDUSTRIES.map(
    (industry) => ({
      value: industry.id,
      labelKey: industry.labelKey,
      descriptionKey: industry.hintKey,
      icon: industry.icon,
    })
  );

  /**
   * Gewähltes Profil — `null`, solange der Besucher nichts angeklickt hat und nichts aus der
   * Herkunft ableitbar war. Bewusst nicht vorbelegt: Eine Kachel, die von selbst leuchtet, ohne
   * dass sie zur Herkunft passt, ist eine Behauptung über den Besucher.
   */
  selectedIndustry: DemoIndustryId | null = null;

  constructor(
    private demoService: DemoService,
    route: ActivatedRoute,
    public themeService: ThemeService,
    private cdr: ChangeDetectorRef,
    private logger: ClientLoggerService
  ) {
    this.attribution = demoAttributionFromQueryParams(route.snapshot.queryParams);
    // Kam der Besucher von einer Branchen-Landingpage (`/industries/<slug>`), ist die Auswahl
    // bereits getroffen — dann ist die Kachelreihe eine Bestätigung und keine Abfrage.
    //
    // `?branche=…` hat Vorrang vor `pg`: `pg` trägt nur die Seite des Klicks, und der erfolgt
    // regelmäßig auf `/pricing` oder `/mobile-app`, nachdem die Branchenseite längst gelesen
    // war. Welche das war, weiß ausschließlich die Landingpage — sie muss den Wert mitgeben
    // (siehe `demoIndustryFromParam`). Fehlt der Parameter, bleibt es beim bisherigen Weg.
    this.selectedIndustry =
      demoIndustryFromParam(route.snapshot.queryParams['branche']) ??
      demoIndustryFromLandingPage(this.attribution.LandingPage);
  }

  /**
   * True, sobald der Besucher ohne Branchenwahl zu starten versucht hat.
   *
   * Erst ab dann wird die Kachelreihe rot markiert — eine Seite, die schon beim Öffnen einen
   * Fehler anzeigt, beschuldigt den Besucher für etwas, das er noch nicht tun konnte.
   */
  industryMissing = false;

  /**
   * Profil, mit dem die Demo startet — `null`, solange nichts gewählt ist.
   *
   * Bewusst kein Rückfall auf das neutrale Profil: Die Branche ist ein Pflichtfeld, und ein
   * stiller Standardwert würde genau die Personalisierung aushebeln, für die die Kachelreihe
   * da ist. `DEMO_INDUSTRY_DEFAULT` bleibt der Rückfall in {@link DemoService.getIndustry}
   * für einen unlesbaren Eintrag im Browser-Speicher — nicht für den Start.
   */
  get effectiveIndustry(): DemoIndustryId | null {
    return this.selectedIndustry;
  }

  /**
   * Der Startknopf ist gedämpft, solange keine Branche gewählt ist — aber **klickbar**.
   *
   * ⚠️ Bewusst `subdued` und nicht `disabled`/`disabledReason`: `XdsButtonComponent.onClick`
   * ruft bei beiden `stopPropagation()`, das Klick-Ereignis erreicht also niemanden. Ein hart
   * gesperrter Knopf könnte die Fehlermeldung, die er auslösen soll, nie auslösen — er wäre
   * genau der stumme Zustand, den die Bot-Prüfung dieser Seite gerade erst beseitigt hat.
   * `subdued` dämpft nur die Optik (`opacity: .62`) und lässt den Klick durch.
   */
  get startSubdued(): boolean {
    return this.selectedIndustry === null;
  }

  selectIndustry(id: DemoIndustryId): void {
    this.selectedIndustry = id;
    // Die Wahl räumt die Beanstandung sofort weg — ein Fehler, der nach seiner Behebung
    // stehen bleibt, ist schlimmer als keiner.
    this.industryMissing = false;
    this.cdr.markForCheck();
  }

  ngAfterViewInit(): void {
    // Der Turnstile-Container existiert erst nach dem Rendern des Templates.
    this.loadTurnstileScript();
  }

  ngOnDestroy(): void {
    this.clearCaptchaTimeout();
    const turnstile = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
    if (turnstile && this.turnstileWidgetId !== null) {
      turnstile.remove(this.turnstileWidgetId);
    }
    this.turnstileWidgetId = null;
    this.turnstileToken = null;

    if (this.turnstileScript && document.head.contains(this.turnstileScript)) {
      document.head.removeChild(this.turnstileScript);
    }
  }

  /**
   * True, wenn kein Captcha konfiguriert ist oder ein gültiges Turnstile-Token vorliegt.
   * Bleibt die harte Bedingung für den Start — die Bot-Bremse wird durch die bessere Anzeige
   * NICHT gelockert (sonst legt ein Skript beliebig viele Demo-Firmen an).
   */
  get captchaSolved(): boolean {
    return !environment.turnstileSiteKey || !!this.turnstileToken;
  }

  /** Der Startknopf ist gesperrt, solange die Prüfung nicht bestanden ist. */
  get startDisabled(): boolean {
    return this.isSubmitting || !this.captchaSolved;
  }

  /** Erklärender Text unter dem Knopf; `null`, wenn alles bereit ist. */
  get captchaHintKey(): string | null {
    switch (this.captchaState) {
      case 'loading':
        return 'DEMO.CAPTCHA_CHECKING';
      case 'interactive':
        return 'DEMO.CAPTCHA_INTERACTIVE';
      case 'failed':
        return 'DEMO.CAPTCHA_FAILED_HINT';
      default:
        return null;
    }
  }

  /** Beschriftung des Startknopfes — nennt den Grund der Sperre statt sie zu verschweigen. */
  get startLabelKey(): string {
    if (this.isSubmitting) {
      return 'DEMO.WELCOME_STARTING';
    }
    if (this.captchaState === 'loading' || this.captchaState === 'interactive') {
      return 'DEMO.WELCOME_START_CHECKING';
    }
    return 'DEMO.WELCOME_START';
  }

  /** True, wenn dem Besucher ein Knopf zum erneuten Anlauf angeboten wird. */
  get showCaptchaRetry(): boolean {
    return this.captchaState === 'failed' && !this.isSubmitting;
  }

  /**
   * Neuer Anlauf der Bot-Prüfung — auf Wunsch des Besuchers, nicht automatisch.
   * Ab jetzt wird das Widget **sichtbar** gerendert (`appearance: 'always'`): Wenn Turnstile eine
   * Interaktion braucht oder die stille Prüfung scheitert, muss der Besucher sie auch sehen können.
   */
  retryCaptcha(): void {
    if (!environment.turnstileSiteKey) {
      return;
    }
    this.forceVisibleWidget = true;
    this.turnstileToken = null;
    this.turnstileRenderAttempts = 0;
    this.setCaptchaState('loading');

    const turnstile = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
    if (turnstile && this.turnstileWidgetId !== null) {
      turnstile.remove(this.turnstileWidgetId);
      this.turnstileWidgetId = null;
    }
    this.renderTurnstileWhenReady();
  }

  onStart(): void {
    if (this.isSubmitting || !this.captchaSolved) {
      return;
    }

    // Pflichtfeld: ohne Branche kein Start. Der Besucher bekommt die Beanstandung an der
    // Kachelreihe zu sehen, nicht am Knopf — dort ist die fehlende Eingabe.
    if (this.selectedIndustry === null) {
      this.industryMissing = true;
      this.cdr.markForCheck();
      this.focusIndustryChoice();
      return;
    }

    this.isSubmitting = true;
    this.demoService
      .start(this.turnstileToken, this.attribution, this.selectedIndustry)
      .subscribe((success) => {
        if (success) {
          // Navigation erfolgt im AuthService.startDemoSession.
          return;
        }
        // Fehlgeschlagen: Turnstile-Token ist verbraucht → neu anfordern, Button reaktivieren.
        this.isSubmitting = false;
        this.resetTurnstile();
        this.cdr.markForCheck();
      });
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  /**
   * Setzt den Fokus auf die erste Kachel und holt sie ins Bild.
   *
   * Die Meldung allein genügt nicht: Auf einem kleinen Telefon kann die Kachelreihe beim Klick
   * auf den Startknopf schon aus dem Bild gescrollt sein — dann erschiene eine Beanstandung,
   * die niemand sieht. Der Fokus bringt zusätzlich Tastatur- und Vorlesehilfen-Nutzer an die
   * Stelle, an der etwas fehlt.
   */
  private focusIndustryChoice(): void {
    const erste = document.querySelector<HTMLElement>('[role="radiogroup"] [role="radio"]');
    erste?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    erste?.focus();
  }

  getThemeIconClass(): string {
    const mode = this.themeService.getThemeIcon();
    if (mode === 'brightness_auto') return 'fa-circle-half-stroke';
    if (mode === 'light_mode') return 'fa-sun';
    return 'fa-moon';
  }

  private loadTurnstileScript(): void {
    if (!environment.turnstileSiteKey) {
      return;
    }

    const turnstile = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
    if (turnstile) {
      this.renderTurnstileWhenReady();
      return;
    }

    if (
      !document.querySelector(
        'script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]'
      )
    ) {
      this.turnstileScript = document.createElement('script');
      this.turnstileScript.src =
        'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      this.turnstileScript.async = true;
      this.turnstileScript.defer = true;
      document.head.appendChild(this.turnstileScript);
    }

    this.renderTurnstileWhenReady();
  }

  private renderTurnstileWhenReady(): void {
    if (this.turnstileWidgetId !== null) {
      return;
    }

    const turnstile = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
    const container = document.getElementById('demo-turnstile-container');

    if (turnstile && container) {
      this.startCaptchaTimeout();
      this.turnstileWidgetId = turnstile.render(container, {
        sitekey: environment.turnstileSiteKey,
        appearance: this.forceVisibleWidget ? 'always' : 'interaction-only',
        theme: 'auto',
        callback: (token: string) => {
          this.turnstileToken = token;
          this.setCaptchaState('ready');
        },
        'expired-callback': () => {
          // Token ist abgelaufen (Turnstile-Tokens leben ~5 Min). Still neu anfordern.
          this.turnstileToken = null;
          this.setCaptchaState('loading');
        },
        'error-callback': () => this.failCaptcha('error-callback'),
        // Challenge wird sichtbar und wartet auf den Menschen → kein Zeitgeber.
        'before-interactive-callback': () => this.setCaptchaState('interactive'),
        'after-interactive-callback': () => {
          if (!this.turnstileToken) {
            this.setCaptchaState('loading');
          }
        },
        'timeout-callback': () => this.failCaptcha('timeout-callback'),
        'unsupported-callback': () => this.failCaptcha('browser-unsupported'),
      });
      return;
    }

    // Script/Container noch nicht bereit → begrenzt erneut versuchen (max. ~5 s).
    if (this.turnstileRenderAttempts < 50) {
      this.turnstileRenderAttempts++;
      setTimeout(() => this.renderTurnstileWhenReady(), 100);
      return;
    }

    // Skript ist nach ~5 s nicht da: fast immer ein Blocker oder eine gesperrte Cloudflare-Domain.
    this.failCaptcha('script-unavailable');
  }

  /**
   * Bot-Prüfung endgültig fehlgeschlagen: Zustand setzen **und** melden.
   *
   * Die Meldung ist der eigentliche Punkt. Bis 2026-08-19 endete jeder Fehlschlag in einem
   * leeren `error-callback` — der Besuch sah im Zugriffsprotokoll aus wie „hat sich anders
   * überlegt", und nichts unterschied ihn von einem Bot ohne Absicht. Bewusst `logWarning`:
   * Die Stichprobe der Demo-Instanz (`ClientLogging:SamplingRate`) verwirft Info-Meldungen,
   * Warnungen aber nie — der Einzelfall geht also nicht verloren.
   */
  private failCaptcha(reason: string): void {
    this.turnstileToken = null;
    this.setCaptchaState('failed');
    this.logger.logWarning('Demo-Bot-Prüfung nicht abgeschlossen', LogCategory.Security, {
      Reason: reason,
      Cta: this.attribution.Cta ?? null,
      LandingPage: this.attribution.LandingPage ?? null,
      JourneyId: this.attribution.JourneyId ?? null,
      WidgetVisible: this.forceVisibleWidget,
    });
  }

  private resetTurnstile(): void {
    this.turnstileToken = null;
    const turnstile = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
    if (turnstile && this.turnstileWidgetId !== null) {
      turnstile.reset(this.turnstileWidgetId);
    }
    this.setCaptchaState(environment.turnstileSiteKey ? 'loading' : 'not-required');
  }

  /** Setzt den Zustand, hält den Zeitgeber konsistent und stößt die Anzeige an. */
  private setCaptchaState(state: DemoCaptchaState): void {
    this.captchaState = state;
    if (state === 'loading') {
      this.startCaptchaTimeout();
    } else {
      this.clearCaptchaTimeout();
    }
    this.cdr.markForCheck();
  }

  private startCaptchaTimeout(): void {
    this.clearCaptchaTimeout();
    this.captchaTimeoutId = setTimeout(() => {
      this.captchaTimeoutId = null;
      if (!this.turnstileToken) {
        this.failCaptcha('no-token-within-timeout');
      }
    }, CAPTCHA_TIMEOUT_MS);
  }

  private clearCaptchaTimeout(): void {
    if (this.captchaTimeoutId !== null) {
      clearTimeout(this.captchaTimeoutId);
      this.captchaTimeoutId = null;
    }
  }
}
